import React, { useState, useMemo, useCallback, useTransition } from "react";
import { Layout } from "antd";
import { Helmet } from "react-helmet";
import PlaybackSidebar from "./PlaybackSidebar/PlaybackSidebar";
import HistorySidebar from "./HistorySidebar/HistorySidebar";
import PlaybackVideo from "./PlaybackVideo/PlaybackVideo";
import {
  useGetModDataQuery,
  useStartPlaybackHLSMutation,
} from "../../Redux/service/mod-streaming/ModData";
import usePermission from "../../hooks/usePermission";
import AccessDenied from "../AccessDenied";
import "./ModPlayback.css";
import BreadCrumb from "../Header/SecondHeader";

const { Content } = Layout;

const ModPlayback = () => {
  const { view } = usePermission("MODPLAYBACK");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Prepare API query parameters - ensure serializable for RTK Query cache key
  const queryParams = useMemo(() => {
    const params = {};
    if (searchQuery && searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    if (selectedGroup && selectedGroup !== "all") {
      // Handle "ungrouped" option
      params.groupName =
        selectedGroup === "ungrouped" ? "ungrouped" : selectedGroup;
    }
    // Return empty object if no params to ensure consistent cache key
    return Object.keys(params).length > 0 ? params : {};
  }, [searchQuery, selectedGroup]);

  const {
    data: modData,
    isLoading: modDataLoading,
    isFetching: modDataFetching,
  } = useGetModDataQuery(queryParams, {
    // Refetch when parameters change
    refetchOnMountOrArgChange: true,
  });
  const [startPlaybackHLS] = useStartPlaybackHLSMutation();

  // Extract groups and devices from the transformed data
  const groupsData = modData?.groups || [];
  const vehiclesData = modData?.devices || [];
  // Show loading during initial load or when fetching with new parameters
  const vehiclesLoading = modDataLoading || modDataFetching;
  const groupsLoading = modDataLoading || modDataFetching;
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [dateFilter, setDateFilter] = useState("today"); // "today", "yesterday", "custom"
  const [customDateRange, setCustomDateRange] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [isLoadingPlayback, setIsLoadingPlayback] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isPending, startTransition] = useTransition();

  // Process vehicles data
  const processedVehicles = useMemo(() => {
    if (!vehiclesData || vehiclesData.length === 0) return [];

    return vehiclesData.map((vehicle) => {
      // Use channels from mdvr if available, otherwise generate default channels
      const channels = [];
      const mdvr = vehicle.mdvr || {};
      const mdvrChannels = mdvr.channels || [];
      const channelCount = mdvr.channelCount || mdvrChannels.length || 4;

      if (mdvrChannels.length > 0) {
        // Use actual channels from mdvr
        mdvrChannels.forEach((channel) => {
          channels.push({
            number: channel.channelNumber,
            name: channel.name || `CH${channel.channelNumber}`,
            position: channel.position,
            isActive: channel.isActive,
            hlsUrl:
              vehicle.hlsUrl ||
              `blob:https://stream.softsprint.ae/c3c0960c-0a96-4325-a4dc-958083bcb6cf`,
          });
        });
      } else {
        // Generate default channels if none available
        for (let i = 1; i <= channelCount; i++) {
          channels.push({
            number: i,
            name: `CH${i}`,
            hlsUrl:
              vehicle.hlsUrl ||
              `blob:https://stream.softsprint.ae/c3c0960c-0a96-4325-a4dc-958083bcb6cf`,
          });
        }
      }

      const imei = vehicle.uniqueId || vehicle.imei || vehicle.id || "";

      return {
        id: vehicle.id,
        name: vehicle.name || imei,
        imei: imei,
        status: vehicle.status || "offline",
        channels,
        groupId: vehicle.groupId || null,
        mdvr: mdvr,
      };
    });
  }, [vehiclesData]);

  // Filter vehicles based on status (search and group are handled by API)
  const filteredVehicles = useMemo(() => {
    let filtered = processedVehicles;

    // Status filter (client-side since API doesn't handle it)
    if (selectedStatus !== "all") {
      filtered = filtered.filter(
        (vehicle) =>
          vehicle.status.toLowerCase() === selectedStatus.toLowerCase(),
      );
    }

    return filtered;
  }, [processedVehicles, selectedStatus]);

  // Group vehicles by group
  const groupedVehicles = useMemo(() => {
    const grouped = {};
    filteredVehicles.forEach((vehicle) => {
      const groupKey = vehicle.groupId ? String(vehicle.groupId) : "ungrouped";
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(vehicle);
    });
    return grouped;
  }, [filteredVehicles]);

  // Check if a channel is selected (single selection mode)
  const isChannelActive = useCallback(
    (vehicleId, channelNumber) => {
      return (
        selectedVehicle?.id === vehicleId &&
        selectedChannel?.number === channelNumber
      );
    },
    [selectedVehicle, selectedChannel],
  );

  // Handle channel selection - single selection mode
  const handleChannelClick = useCallback(
    (vehicle, channel) => {
      startTransition(() => {
        if (
          selectedVehicle?.id === vehicle.id &&
          selectedChannel?.number === channel.number
        ) {
          // Deselect if clicking the same channel
          setSelectedVehicle(null);
          setSelectedChannel(null);
          setSelectedSegment(null);
          setPlaybackUrl(null);
        } else {
          // Select new channel
          setSelectedVehicle(vehicle);
          setSelectedChannel(channel);
          setSelectedSegment(null);
          setPlaybackUrl(null);
        }
      });
    },
    [selectedVehicle, selectedChannel, startTransition],
  );

  // Handle segment selection - start playback
  const handleSegmentSelect = useCallback(
    async (segment) => {
      if (!segment || !selectedVehicle || !selectedChannel) return;

      // If clicking the same segment, deselect it
      if (selectedSegment?.id === segment.id) {
        setSelectedSegment(null);
        setPlaybackUrl(null);
        return;
      }

      setSelectedSegment(segment);
      setIsLoadingPlayback(true);
      setPlaybackUrl(null);

      try {
        const response = await startPlaybackHLS({
          imei: selectedVehicle.imei,
          channel: selectedChannel.number,
          startTime: segment.startTimeISO,
          endTime: segment.endTimeISO,
        }).unwrap();

        if (response.success && response.urls?.hls) {
          setPlaybackUrl(response.urls.hls);
        } else {
          console.error("Failed to start playback:", response);
          setSelectedSegment(null);
        }
      } catch (error) {
        console.error("Error starting playback:", error);
        setSelectedSegment(null);
      } finally {
        setIsLoadingPlayback(false);
      }
    },
    [selectedVehicle, selectedChannel, selectedSegment, startPlaybackHLS],
  );

  if (!view) {
    return <AccessDenied />;
  }

  return (
    <>
      <Helmet>
        <title>History Playback - Fleet Command</title>
        <meta name="description" content="History video playback module" />
      </Helmet>
      <BreadCrumb />

      <div className="mod-playback-wrapper">
        <Layout className="mod-playback-container">
          <PlaybackSidebar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            vehicles={filteredVehicles}
            groupedVehicles={groupedVehicles}
            groups={groupsData || []}
            onChannelClick={handleChannelClick}
            isChannelActive={isChannelActive}
            isLoading={vehiclesLoading || groupsLoading}
          />
          {selectedVehicle && selectedChannel && (
            <HistorySidebar
              selectedVehicle={selectedVehicle}
              selectedChannel={selectedChannel}
              dateFilter={dateFilter}
              setDateFilter={setDateFilter}
              customDateRange={customDateRange}
              setCustomDateRange={setCustomDateRange}
              selectedSegment={selectedSegment}
              setSelectedSegment={handleSegmentSelect}
            />
          )}
          <Content className="mod-playback-body">
            <div className="playback-body-content">
              <PlaybackVideo
                selectedVehicle={selectedVehicle}
                selectedChannel={selectedChannel}
                selectedSegment={selectedSegment}
                playbackUrl={playbackUrl}
                isLoadingPlayback={isLoadingPlayback}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
              />
            </div>
          </Content>
        </Layout>
      </div>
    </>
  );
};

export default ModPlayback;
